import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  collection,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";

// ─────────────────────────────────────────────────────────────
// OATH TEXTS — 4 roles × 3 languages (finalized & verified)
// Placeholders: {NAME}, {FATHER_NAME} substituted at runtime
// ─────────────────────────────────────────────────────────────
const ROLE_LABEL = {
  President: { en: "President", te: "అధ్యక్షుడు", hi: "अध्यक्ष" },
  Secretary: { en: "Secretary", te: "కార్యదర్శి", hi: "सचिव" },
  Treasurer: { en: "Treasurer", te: "కోశాధికారి", hi: "कोषाध्यक्ष" },
  Advisor:   { en: "Advisor",   te: "సలహాదారు", hi: "सलाहकार" },
};

const OATH_EN = (name, father, role, societyName) =>
`"I, ${name} S/o. ${father}, do swear/solemnly affirm as ${role} of ${societyName}, that I will be faithful and bear true allegiance to the byelaws of ${societyName}, which are governed by India and to the Constitution of India as by law established, that I will uphold the sovereignty and integrity of our association, and that I will carry out the duties of my office loyally, honestly, with impartiality and maintain confidentiality.
So help me God"`;

const OATH_TE = {
  President: (name, father, societyName) =>
`"నేను, ${name} S/o. ${father}, ${societyName} అధ్యక్షుడిగా ప్రమాణం చేస్తున్నాను/గంభీరంగా ధృవీకరిస్తున్నాను, భారతదేశం ద్వారా నిర్వహించబడే ${societyName} యొక్క బైలాలకు మరియు చట్టం ద్వారా స్థాపించబడిన భారత రాజ్యాంగానికి నేను నమ్మకంగా ఉంటాను మరియు నిజమైన విధేయతను కలిగి ఉంటాను, మా సంఘం యొక్క సార్వభౌమత్వాన్ని మరియు సమగ్రతను నేను సమర్థిస్తాను మరియు నా కార్యాలయం యొక్క విధులను విధేయతతో, నిజాయితీగా, నిష్పాక్షికంగా మరియు గోప్యతను కాపాడుతాను.
కాబట్టి నాకు సహాయం చేయు దేవుడా"`,
  Secretary: (name, father, societyName) =>
`"నేను, ${name} S/o. ${father}, ${societyName} కార్యదర్శిగా ప్రమాణం చేస్తున్నాను/గంభీరంగా ధృవీకరిస్తున్నాను, భారతదేశం ద్వారా నిర్వహించబడే ${societyName} యొక్క బైలాలకు మరియు చట్టం ద్వారా స్థాపించబడిన భారత రాజ్యాంగానికి నేను నమ్మకంగా ఉంటాను మరియు నిజమైన విధేయతను కలిగి ఉంటాను, మా అసోసియేషన్ యొక్క సార్వభౌమత్వాన్ని మరియు సమగ్రతను నేను సమర్థిస్తాను మరియు నా కార్యాలయ విధులను విధేయతతో, నిజాయితీగా, నిష్పాక్షికంగా మరియు గోప్యతను కాపాడుతాను.
కాబట్టి నాకు సహాయం చేయు దేవుడా"`,
  Treasurer: (name, father, societyName) =>
`"నేను, ${name} S/o. ${father}, ${societyName} కోశాధికారిగా ప్రమాణం చేస్తున్నాను/గంభీరంగా ధృవీకరిస్తున్నాను, భారతదేశం ద్వారా నిర్వహించబడే ${societyName} యొక్క బైలాలకు మరియు చట్టం ద్వారా స్థాపించబడిన భారత రాజ్యాంగానికి నేను నమ్మకంగా ఉంటాను మరియు నిజమైన విధేయతను కలిగి ఉంటాను, మా సంఘం యొక్క సార్వభౌమత్వాన్ని మరియు సమగ్రతను నేను సమర్థిస్తాను మరియు నా కార్యాలయం యొక్క విధులను విధేయతతో, నిజాయితీగా, నిష్పాక్షికంగా మరియు గోప్యతను కాపాడుతాను.
కాబట్టి నాకు సహాయం చేయు దేవుడా"`,
  Advisor: (name, father, societyName) =>
`"నేను, ${name} S/o. ${father}, ${societyName} సలహాదారుగా ప్రమాణం చేస్తున్నాను/గంభీరంగా ధృవీకరిస్తున్నాను, భారతదేశం ద్వారా నిర్వహించబడే ${societyName} యొక్క బైలాలకు మరియు చట్టం ద్వారా స్థాపించబడిన భారత రాజ్యాంగానికి నేను నమ్మకంగా ఉంటాను మరియు నిజమైన విధేయతను కలిగి ఉంటాను, మా సంఘం యొక్క సార్వభౌమత్వాన్ని మరియు సమగ్రతను నేను సమర్థిస్తాను మరియు నా కార్యాలయ విధులను విధేయతతో, నిజాయితీగా, నిష్పాక్షికంగా మరియు గోప్యతను కాపాడుతాను.
కాబట్టి నాకు సహాయం చేయు దేవుడా"`,
};

const OATH_HI = {
  President: (name, father, societyName) =>
`"मैं, ${name} पुत्र ${father}, ${societyName} के अध्यक्ष के रूप में शपथ लेता/सत्यनिष्ठा से प्रतिज्ञान करता हूं कि मैं ${societyName} के उपनियमों, जो भारत द्वारा शासित हैं और भारत के संविधान के प्रति वफादार रहूंगा और सच्ची निष्ठा रखूंगा, मैं अपने संघ की संप्रभुता और अखंडता को बनाए रखूंगा, और मैं अपने पद के कर्तव्यों को निष्ठापूर्वक, ईमानदारी से, निष्पक्षता के साथ पूरा करूंगा और गोपनीयता बनाए रखूंगा।
भगवान मेरी मदद करें"`,
  Secretary: (name, father, societyName) =>
`"मैं, ${name} पुत्र ${father}, ${societyName} के सचिव के रूप में शपथ लेता/सत्यनिष्ठा से प्रतिज्ञान करता हूं कि मैं ${societyName} के उपनियमों, जो भारत द्वारा शासित हैं और भारत के संविधान के प्रति वफादार रहूंगा और सच्ची निष्ठा रखूंगा, मैं अपने संघ की संप्रभुता और अखंडता को बनाए रखूंगा, और मैं अपने पद के कर्तव्यों को निष्ठापूर्वक, ईमानदारी से, निष्पक्षता के साथ पूरा करूंगा और गोपनीयता बनाए रखूंगा।
भगवान मेरी मदद करें"`,
  Treasurer: (name, father, societyName) =>
`"मैं, ${name} पुत्र ${father}, ${societyName} के कोषाध्यक्ष के रूप में शपथ लेता/सत्यनिष्ठा से प्रतिज्ञान करता हूं कि मैं ${societyName} के उपनियमों, जो भारत द्वारा शासित हैं और भारत के संविधान के प्रति वफादार रहूंगा और सच्ची निष्ठा रखूंगा, मैं अपने एसोसिएशन की संप्रभुता और अखंडता को बनाए रखूंगा, और मैं अपने पद के कर्तव्यों को निष्ठापूर्वक, ईमानदारी से, निष्पक्षता के साथ पूरा करूंगा और गोपनीयता बनाए रखूंगा।
भगवान मेरी मदद करें"`,
  Advisor: (name, father, societyName) =>
`"मैं, ${name} पुत्र ${father}, ${societyName} के सलाहकार के रूप में शपथ लेता/सत्यनिष्ठा से प्रतिज्ञान करता हूं कि मैं ${societyName} के उपनियमों, जो भारत द्वारा शासित हैं और भारत के संविधान के प्रति वफादार रहूंगा और सच्ची निष्ठा रखूंगा, मैं अपने संघ की संप्रभुता और अखंडता को बनाए रखूंगा, और मैं अपने पद के कर्तव्यों को निष्ठापूर्वक, ईमानदारी से, निष्पक्षता के साथ पूरा करूंगा और गोपनीयता बनाए रखूंगा।
भगवान मेरी मदद करें"`,
};

const LANG_LABEL = { en: "English", te: "తెలుగు (Telugu)", hi: "हिंदी (Hindi)" };
// Advisor hidden temporarily — to re-enable, add "Advisor" back to the array below
// const OATH_ROLES = ["President", "Secretary", "Treasurer", "Advisor"];
const OATH_ROLES = ["President", "Secretary", "Treasurer"];

function getOathText(role, lang, name, father, societyName) {
  const roleLabel = ROLE_LABEL[role]?.[lang] || role;
  const displayName = societyName || "the society";
  if (lang === "te" && OATH_TE[role]) return OATH_TE[role](name, father, displayName);
  if (lang === "hi" && OATH_HI[role]) return OATH_HI[role](name, father, displayName);
  return OATH_EN(name, father, roleLabel, displayName);
}

const inputStyle = {
  width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
  borderRadius: "10px", padding: "10px 12px", color: "#e8e8f0",
  fontSize: "14px", outline: "none", boxSizing: "border-box",
};
const labelStyle = {
  fontSize: "11px", color: "#8888a8", marginBottom: "5px", display: "block",
};
const sectionStyle = {
  background: "#1a1a24", border: "1px solid #2e2e40",
  borderRadius: "14px", padding: "16px", marginBottom: "14px",
};
const btnPrimary = {
  background: "#6c63ff", border: "none", borderRadius: "10px",
  padding: "10px 16px", color: "#fff", fontSize: "13px",
  fontWeight: "600", cursor: "pointer",
};
const btnGhost = {
  background: "transparent", border: "1px solid #2e2e40", borderRadius: "10px",
  padding: "10px 16px", color: "#c0c0d0", fontSize: "13px",
  fontWeight: "600", cursor: "pointer",
};

// ─────────────────────────────────────────────────────────────
// SECTION 1 — Bylaws PDF (everyone)
// ─────────────────────────────────────────────────────────────
export function BylawsPDFSection() {
  const { isPresident } = useAuth();
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [societyName, setSocietyName] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, "bylaws_docs", "bylaws_pdf"));
        if (snap.exists()) setPdfDoc(snap.data());

        // Society name — same field SetupWizard saves to settings/init
        const settingsSnap = await getDoc(doc(db, "settings", "init"));
        if (settingsSnap.exists()) {
          setSocietyName(settingsSnap.data().societyName || "");
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function handleDownload() {
    if (!pdfDoc?.base64) return;
    const link = document.createElement("a");
    link.href = `data:application/pdf;base64,${pdfDoc.base64}`;
    link.download = pdfDoc.fileName || `${societyName || "Society"}_Bylaws.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { setErr("Please select a PDF file."); return; }
    if (file.size > 4 * 1024 * 1024) { setErr("File too large. Max ~4MB recommended for Firestore."); return; }

    setUploading(true);
    setErr("");
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(",")[1];
        await setDoc(doc(db, "bylaws_docs", "bylaws_pdf"), {
          fileName: file.name,
          base64,
          uploadedAt: serverTimestamp(),
        });
        setPdfDoc({ fileName: file.name, base64 });
        setUploading(false);
      };
      reader.onerror = () => { setErr("Failed to read file."); setUploading(false); };
      reader.readAsDataURL(file);
    } catch (e) {
      setErr("Upload failed. Try again.");
      setUploading(false);
    }
  }

  return (
    <div style={sectionStyle}>
      <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0", marginBottom: "4px" }}>
        📜 Your Society Bylaws
      </div>
      <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "12px" }}>
        Visible to everyone — MC, Flat Owners, Tenants & Family Members
      </div>

      {loading ? (
        <div style={{ fontSize: "12px", color: "#555570" }}>Loading…</div>
      ) : pdfDoc ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
          <div style={{ fontSize: "12px", color: "#c0c0d0" }}>
            📄 {pdfDoc.fileName || `${societyName || "Society"}_Bylaws.pdf`}
          </div>
          <button onClick={handleDownload} style={btnPrimary}>⬇ Download PDF</button>
        </div>
      ) : (
        <div style={{ fontSize: "12px", color: "#555570" }}>
          No bylaws document uploaded yet.
        </div>
      )}

      {isPresident() && (
        <div style={{ marginTop: "14px", paddingTop: "14px", borderTop: "1px solid #2e2e40" }}>
          <label style={labelStyle}>
            {pdfDoc ? "Replace PDF (President only)" : "Upload Bylaws PDF (President only)"}
          </label>
          <input
            type="file" accept="application/pdf"
            onChange={handleUpload} disabled={uploading}
            style={{ fontSize: "12px", color: "#c0c0d0" }}
          />
          {uploading && <div style={{ fontSize: "11px", color: "#6c63ff", marginTop: "6px" }}>Uploading…</div>}
          {err && <div style={{ fontSize: "11px", color: "#ff5252", marginTop: "6px" }}>{err}</div>}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SECTION 2 — MC Oath (forthcoming/current MC roles only)
// ─────────────────────────────────────────────────────────────
function MCOathSection() {
  const { user, isPresident } = useAuth();
  const isOathEligible = OATH_ROLES.includes(user?.role);

  const [myRecord, setMyRecord] = useState(null);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [societyName, setSocietyName] = useState("");
  // Whether this society has Oath-Taking turned on (President's choice, set in SetupWizard).
  // If the setting is missing entirely, we treat it as ON (agreed default).
  const [oathEnabled, setOathEnabled] = useState(true);

  const [role, setRole] = useState(user?.role || "");
  const [lang, setLang] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [showOath, setShowOath] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    async function load() {
      try {
        // Society name — same field SetupWizard saves to settings/init
        const settingsSnap = await getDoc(doc(db, "settings", "init"));
        if (settingsSnap.exists()) {
          const settingsData = settingsSnap.data();
          setSocietyName(settingsData.societyName || "");
          // Only an explicit "false" turns this off. true, missing, or
          // anything else defaults to on, per agreed design.
          setOathEnabled(settingsData.oathEnabled !== false);
        }

        if (isOathEligible) {
          const snap = await getDocs(
            query(collection(db, "oathRecords"), where("uid", "==", user.uid))
          );
          if (!snap.empty) setMyRecord(snap.docs[0].data());
        }
        if (isPresident()) {
          const snap = await getDocs(collection(db, "oathRecords"));
          setAllRecords(snap.docs.map(d => ({ docId: d.id, ...d.data() })));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleConfirm() {
    if (!fatherName.trim()) { setErr("Please enter father's name."); return; }
    if (!lang) { setErr("Please select a language."); return; }
    setConfirming(true);
    setErr("");
    try {
      const oathTextEN = getOathText(role, "en", user.name, fatherName.trim(), societyName);
      const oathTextRegional = lang === "en" ? oathTextEN : getOathText(role, lang, user.name, fatherName.trim(), societyName);

      const record = {
        uid: user.uid,
        name: user.name,
        role,
        flatId: user.flatId || null,
        fatherName: fatherName.trim(),
        regionalLang: lang,
        oathTextEN,
        oathTextRegional,
        confirmedAt: serverTimestamp(),
      };
      await addDoc(collection(db, "oathRecords"), record);
      setMyRecord(record);
    } catch (e) {
      setErr("Failed to save. Try again.");
    } finally {
      setConfirming(false);
    }
  }

  if (loading) {
    return (
      <div style={sectionStyle}>
        <div style={{ fontSize: "12px", color: "#555570" }}>Loading oath status…</div>
      </div>
    );
  }

  // Society has turned Oath-Taking off entirely → don't show section to anyone,
  // not even the President.
  if (!oathEnabled) return null;

  // Not MC and not President → don't show section at all
  if (!isOathEligible && !isPresident()) return null;

  return (
    <div style={sectionStyle}>
      <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0", marginBottom: "4px" }}>
        🖋️ Oath of Allegiance
      </div>
      <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "12px" }}>
        For MC members (President, Secretary, Treasurer, Advisor)
      </div>

      {/* Current MC member's own oath flow */}
      {isOathEligible && (
        myRecord ? (
          <div style={{
            background: "#2ecc7118", border: "1px solid #2ecc7140",
            borderRadius: "10px", padding: "12px", color: "#2ecc71", fontSize: "12px",
          }}>
            ✅ You have confirmed your oath of allegiance as {myRecord.role} in {LANG_LABEL[myRecord.regionalLang] || myRecord.regionalLang}.
          </div>
        ) : (
          <div>
            <div style={{
              background: "#ffae0018", border: "1px solid #ffae0040",
              borderRadius: "10px", padding: "10px 12px", marginBottom: "12px",
              color: "#ffae00", fontSize: "12px",
            }}>
              ⏳ Oath pending — please complete your oath of allegiance as {ROLE_LABEL[role]?.en || role}.
            </div>

            {/* Step 1: Role (auto-detected from your profile, editable if incorrect) */}
            <label style={labelStyle}>Step 1 — Your Role</label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
              {OATH_ROLES.map(r => (
                <button key={r} onClick={() => { setRole(r); setShowOath(false); }} style={{
                  ...(role === r ? btnPrimary : btnGhost),
                  padding: "8px 14px", fontSize: "12px",
                }}>
                  {r}
                </button>
              ))}
            </div>
            {role && role !== user?.role && (
              <div style={{ fontSize: "10px", color: "#ffae00", marginBottom: "10px" }}>
                ⚠️ Your profile role is "{user?.role}". You've selected "{role}" for this oath.
              </div>
            )}

            {/* Step 2: Language picker */}
            <label style={labelStyle}>Step 2 — Choose your language</label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
              {Object.entries(LANG_LABEL).map(([code, label]) => (
                <button key={code} onClick={() => { setLang(code); setShowOath(false); }} style={{
                  ...(lang === code ? btnPrimary : btnGhost),
                  padding: "8px 14px", fontSize: "12px",
                }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Step 3: Father's name */}
            <label style={labelStyle}>Step 3 — Father's Name</label>
            <input
              type="text" value={fatherName}
              onChange={e => { setFatherName(e.target.value); setShowOath(false); }}
              placeholder="Enter father's name"
              style={{ ...inputStyle, marginBottom: "14px" }}
            />

            {/* Step 3: Read oath */}
            {!showOath ? (
              <button
                onClick={() => {
                  if (!role) { setErr("Please select your role."); return; }
                  if (!fatherName.trim()) { setErr("Please enter father's name."); return; }
                  if (!lang) { setErr("Please select a language."); return; }
                  setErr("");
                  setShowOath(true);
                }}
                style={btnPrimary}
              >
                Read Oath Text
              </button>
            ) : (
              <div>
                <div style={{
                  background: "#0f0f13", border: "1px solid #2e2e40",
                  borderRadius: "10px", padding: "12px", marginBottom: "10px",
                }}>
                  <div style={{ fontSize: "10px", color: "#6c63ff", fontWeight: "700", marginBottom: "6px" }}>
                    ENGLISH
                  </div>
                  <div style={{ fontSize: "13px", color: "#e8e8f0", lineHeight: "1.7", whiteSpace: "pre-wrap" }}>
                    {getOathText(role, "en", user.name, fatherName.trim(), societyName)}
                  </div>
                </div>

                {lang !== "en" && (
                  <div style={{
                    background: "#0f0f13", border: "1px solid #2e2e40",
                    borderRadius: "10px", padding: "12px", marginBottom: "14px",
                  }}>
                    <div style={{ fontSize: "10px", color: "#6c63ff", fontWeight: "700", marginBottom: "6px" }}>
                      {LANG_LABEL[lang].toUpperCase()}
                    </div>
                    <div style={{ fontSize: "13px", color: "#e8e8f0", lineHeight: "1.7", whiteSpace: "pre-wrap" }}>
                      {getOathText(role, lang, user.name, fatherName.trim(), societyName)}
                    </div>
                  </div>
                )}

                <button onClick={handleConfirm} disabled={confirming} style={{
                  ...btnPrimary, width: "100%",
                  background: confirming ? "#2e2e40" : "#2ecc71",
                }}>
                  {confirming ? "Saving…" : "✓ I Confirm"}
                </button>
              </div>
            )}

            {err && <div style={{ fontSize: "11px", color: "#ff5252", marginTop: "8px" }}>{err}</div>}
          </div>
        )
      )}

      {/* President's view of all records */}
      {isPresident() && (
        <div style={{ marginTop: isOathEligible ? "16px" : "0", paddingTop: isOathEligible ? "16px" : "0", borderTop: isOathEligible ? "1px solid #2e2e40" : "none" }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "#c0c0d0", marginBottom: "8px" }}>
            MC Oath Status
          </div>
          {OATH_ROLES.map(r => {
            const rec = allRecords.find(rr => rr.role === r);
            return (
              <div key={r} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "8px 0", borderBottom: "1px solid #2e2e40", fontSize: "12px",
              }}>
                <div style={{ color: "#c0c0d0" }}>
                  {r}{rec ? ` — ${rec.name}` : ""}
                </div>
                {rec ? (
                  <span style={{ color: "#2ecc71" }}>
                    ✅ Confirmed ({LANG_LABEL[rec.regionalLang] || rec.regionalLang})
                  </span>
                ) : (
                  <span style={{ color: "#ffae00" }}>⏳ Pending</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SECTION 3 — Representative Nomination Form (Flat Owners submit,
// President/Secretary approve or reject)
// ─────────────────────────────────────────────────────────────
function RepresentativeFormSection() {
  const { user, isTenant, isFamilyMember, isPresident, isSecretary } = useAuth();
  const isOwner = !isTenant() && !isFamilyMember() && !!user?.flatId;
  const canApprove = isPresident() || isSecretary();

  const [societyName, setSocietyName] = useState("");
  const [loading, setLoading] = useState(true);

  // Owner's own current nomination (whichever is most recent for their flat)
  const [myNomination, setMyNomination] = useState(null);

  // For President/Secretary — everyone's pending nominations
  const [pendingList, setPendingList] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    ownerIdProof: "",
    repName: "",
    repAadhar: "",
    repPhone: "",
    repEmail: "",
    relationship: "",
  });

  // Reject-reason box state, keyed by which nomination is being rejected
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actingId, setActingId] = useState(null); // nomination currently being approved/rejected (disables buttons)

  useEffect(() => {
    async function load() {
      try {
        const settingsSnap = await getDoc(doc(db, "settings", "init"));
        if (settingsSnap.exists()) {
          setSocietyName(settingsSnap.data().societyName || "");
        }

        if (isOwner) {
          const snap = await getDocs(
            query(
              collection(db, "repNominations"),
              where("flatId", "==", user.flatId),
              orderBy("createdAt", "desc"),
              limit(1)
            )
          );
          if (!snap.empty) {
            setMyNomination({ docId: snap.docs[0].id, ...snap.docs[0].data() });
          }
        }

        if (canApprove) {
          const snap = await getDocs(
            query(collection(db, "repNominations"), where("status", "==", "pending"))
          );
          setPendingList(snap.docs.map(d => ({ docId: d.id, ...d.data() })));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOwner && !canApprove) return null;

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function updatePhone(value) {
    // Digits only, capped at 10 — matches the agreed phone-number limit
    const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
    update("repPhone", digitsOnly);
  }

  function buildLetterText(nomination) {
    const today = new Date().toLocaleDateString("en-IN");
    return `To,
The President,
${societyName || "the Society"} Apartment Owners Association

Subject: Nomination of Representative for AOA Meetings

I, ${nomination.ownerName}, owner of Flat No. ${nomination.flatId || "____"}, hereby nominate the following person as my representative to attend AOA meetings on my behalf:

Representative Name : ${nomination.repName}
Relationship        : ${nomination.relationship}
Aadhar / ID Number  : ${nomination.repAadhar}
Phone               : ${nomination.repPhone}
Email               : ${nomination.repEmail}

My ID Proof (Owner) : ${nomination.ownerIdProof}

Date: ${today}

Signature: ${nomination.ownerName}
Flat No: ${nomination.flatId || "____"}
`;
  }

  function handleDownload(nomination) {
    const text = buildLetterText(nomination);
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Representative_Nomination_${nomination.flatId || "Flat"}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async function handleSubmit() {
    if (!form.repName.trim() || !form.relationship.trim() || !form.repPhone.trim()) {
      setErr("Please fill representative name, relationship and phone.");
      return;
    }
    if (form.repPhone.trim().length !== 10) {
      setErr("Representative's phone number must be exactly 10 digits.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      // Only one active (pending or approved) nomination per flat at a time.
      // Mark any existing one as expired — never deleted.
      const activeSnap = await getDocs(
        query(
          collection(db, "repNominations"),
          where("flatId", "==", user.flatId),
          where("status", "in", ["pending", "approved"])
        )
      );
      for (const d of activeSnap.docs) {
        await updateDoc(doc(db, "repNominations", d.id), { status: "expired" });
      }

      const newDocRef = await addDoc(collection(db, "repNominations"), {
        ownerUid: user.uid,
        ownerName: user.name,
        flatId: user.flatId || null,
        ownerIdProof: form.ownerIdProof,
        repName: form.repName,
        repAadhar: form.repAadhar,
        repPhone: form.repPhone,
        repEmail: form.repEmail,
        relationship: form.relationship,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      // Notify President & Secretary specifically — they're the ones who act on it
      const approverSnap = await getDocs(
        query(collection(db, "users"), where("role", "in", ["President", "Secretary"]))
      );
      for (const a of approverSnap.docs) {
        await addDoc(collection(db, "notifications"), {
          toUid: a.id,
          message: `Flat ${user.flatId || ""} submitted a representative nomination — awaiting your approval.`,
          type: "rep_nomination_pending",
          createdAt: serverTimestamp(),
          isRead: false,
        });
      }

      setMyNomination({
        docId: newDocRef.id,
        ownerUid: user.uid,
        ownerName: user.name,
        flatId: user.flatId || null,
        ...form,
        status: "pending",
      });
      setShowForm(false);
      setForm({ ownerIdProof: "", repName: "", repAadhar: "", repPhone: "", repEmail: "", relationship: "" });
    } catch (e) {
      setErr("Failed to submit. Try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove(nomination) {
    setActingId(nomination.docId);
    try {
      await updateDoc(doc(db, "repNominations", nomination.docId), {
        status: "approved",
        approvedBy: user.name,
        approvedAt: serverTimestamp(),
      });
      await addDoc(collection(db, "notifications"), {
        toUid: nomination.ownerUid,
        message: `Your representative nomination for Flat ${nomination.flatId} has been approved. You can now download the letter.`,
        type: "rep_nomination_approved",
        createdAt: serverTimestamp(),
        isRead: false,
      });
      setPendingList(list => list.filter(n => n.docId !== nomination.docId));
    } catch (e) {
      console.error(e);
    } finally {
      setActingId(null);
    }
  }

  async function handleReject(nomination) {
    if (!rejectReason.trim()) return;
    setActingId(nomination.docId);
    try {
      await updateDoc(doc(db, "repNominations", nomination.docId), {
        status: "rejected",
        rejectionReason: rejectReason.trim(),
        rejectedBy: user.name,
        rejectedAt: serverTimestamp(),
      });
      await addDoc(collection(db, "notifications"), {
        toUid: nomination.ownerUid,
        message: `Your representative nomination for Flat ${nomination.flatId} was rejected. Reason: ${rejectReason.trim()}`,
        type: "rep_nomination_rejected",
        createdAt: serverTimestamp(),
        isRead: false,
      });
      setPendingList(list => list.filter(n => n.docId !== nomination.docId));
      setRejectingId(null);
      setRejectReason("");
    } catch (e) {
      console.error(e);
    } finally {
      setActingId(null);
    }
  }

  return (
    <div style={sectionStyle}>
      <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0", marginBottom: "4px" }}>
        📝 Nomination of Representative
      </div>
      <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "12px" }}>
        Nominate someone to attend AOA meetings on your behalf · President/Secretary approval required
      </div>

      {loading ? (
        <div style={{ fontSize: "12px", color: "#555570" }}>Loading…</div>
      ) : (
        <>
          {/* ── Owner's own nomination status ── */}
          {isOwner && (
            <div style={{ marginBottom: canApprove ? "18px" : "0" }}>
              {!myNomination || myNomination.status === "rejected" || myNomination.status === "expired" ? (
                myNomination?.status === "rejected" ? (
                  <div style={{ marginBottom: "12px" }}>
                    <div style={{
                      background: "#ff525218", border: "1px solid #ff525240",
                      borderRadius: "10px", padding: "12px", color: "#ff5252", fontSize: "12px",
                    }}>
                      ❌ Your last nomination was rejected.<br />
                      Reason: {myNomination.rejectionReason || "No reason given."}
                    </div>
                  </div>
                ) : null
              ) : myNomination.status === "pending" ? (
                <div style={{
                  background: "#ffae0018", border: "1px solid #ffae0040",
                  borderRadius: "10px", padding: "12px", color: "#ffae00", fontSize: "12px",
                }}>
                  ⏳ Submitted — waiting for President/Secretary approval. The letter will be downloadable once approved.
                </div>
              ) : myNomination.status === "approved" ? (
                <div>
                  <div style={{
                    background: "#2ecc7118", border: "1px solid #2ecc7140",
                    borderRadius: "10px", padding: "12px", color: "#2ecc71", fontSize: "12px", marginBottom: "10px",
                  }}>
                    ✅ Approved by {myNomination.approvedBy}. {myNomination.repName} is now your authorized representative.
                  </div>
                  <button onClick={() => handleDownload(myNomination)} style={btnPrimary}>
                    ⬇ Download Letter
                  </button>
                </div>
              ) : null}

              {(!myNomination || myNomination.status === "rejected" || myNomination.status === "expired") && (
                !showForm ? (
                  <button onClick={() => setShowForm(true)} style={btnPrimary}>
                    {myNomination?.status === "rejected" ? "Submit New Nomination" : "Generate Nomination Form"}
                  </button>
                ) : (
                  <div>
                    <div style={{ marginBottom: "10px" }}>
                      <label style={labelStyle}>Your ID Proof (Aadhar / PAN / Passport)</label>
                      <input style={inputStyle} value={form.ownerIdProof}
                        onChange={e => update("ownerIdProof", e.target.value)}
                        placeholder="e.g. Aadhar XXXX-XXXX-1234" />
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <label style={labelStyle}>Representative's Name</label>
                      <input style={inputStyle} value={form.repName}
                        onChange={e => update("repName", e.target.value)} placeholder="Full name" />
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <label style={labelStyle}>Relationship</label>
                      <input style={inputStyle} value={form.relationship}
                        onChange={e => update("relationship", e.target.value)} placeholder="e.g. Brother, Spouse, Relative" />
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <label style={labelStyle}>Representative's ID Proof (Aadhar / PAN / Passport)</label>
                      <input style={inputStyle} value={form.repAadhar}
                        onChange={e => update("repAadhar", e.target.value)} placeholder="e.g. Aadhar XXXX-XXXX-1234" />
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <label style={labelStyle}>Representative's Phone</label>
                      <input style={inputStyle} value={form.repPhone}
                        onChange={e => updatePhone(e.target.value)}
                        inputMode="numeric" maxLength={10}
                        placeholder="10-digit mobile number" />
                    </div>
                    <div style={{ marginBottom: "14px" }}>
                      <label style={labelStyle}>Representative's Email</label>
                      <input style={inputStyle} value={form.repEmail}
                        onChange={e => update("repEmail", e.target.value)} placeholder="email@example.com" />
                    </div>

                    <div style={{
                      background: "#6c63ff18", border: "1px solid #6c63ff30",
                      borderRadius: "10px", padding: "10px 12px", marginBottom: "14px",
                      fontSize: "11px", color: "#8888a8",
                    }}>
                      🔒 Privacy: ID details are shared only with the President/Secretary for approval. Other flat owners only see a general notice once approved.
                    </div>

                    {err && <div style={{ fontSize: "11px", color: "#ff5252", marginBottom: "10px" }}>{err}</div>}

                    <button onClick={handleSubmit} disabled={saving} style={{
                      ...btnPrimary, width: "100%",
                      background: saving ? "#2e2e40" : "#6c63ff",
                    }}>
                      {saving ? "Submitting…" : "Submit for Approval"}
                    </button>
                  </div>
                )
              )}
            </div>
          )}

          {/* ── President/Secretary approval queue ── */}
          {canApprove && (
            <div style={{ paddingTop: isOwner ? "16px" : "0", borderTop: isOwner ? "1px solid #2e2e40" : "none" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", color: "#c0c0d0", marginBottom: "10px" }}>
                Pending Nominations {pendingList.length > 0 ? `(${pendingList.length})` : ""}
              </div>
              {pendingList.length === 0 ? (
                <div style={{ fontSize: "12px", color: "#555570" }}>No nominations waiting for approval.</div>
              ) : (
                pendingList.map(n => (
                  <div key={n.docId} style={{
                    background: "#0f0f13", border: "1px solid #2e2e40",
                    borderRadius: "10px", padding: "12px", marginBottom: "10px",
                  }}>
                    <div style={{ fontSize: "12px", color: "#e8e8f0", marginBottom: "6px" }}>
                      <strong>Flat {n.flatId}</strong> — {n.ownerName}
                    </div>
                    <div style={{ fontSize: "11px", color: "#8888a8", lineHeight: "1.7" }}>
                      Representative: {n.repName} ({n.relationship})<br />
                      ID: {n.repAadhar} · Phone: {n.repPhone} · Email: {n.repEmail}<br />
                      Owner ID Proof: {n.ownerIdProof}
                    </div>

                    {rejectingId === n.docId ? (
                      <div style={{ marginTop: "10px" }}>
                        <input
                          style={inputStyle}
                          value={rejectReason}
                          onChange={e => setRejectReason(e.target.value)}
                          placeholder="Reason for rejection (shown to the owner)"
                        />
                        <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                          <button
                            onClick={() => handleReject(n)}
                            disabled={!rejectReason.trim() || actingId === n.docId}
                            style={{ ...btnPrimary, background: "#ff5252" }}
                          >
                            {actingId === n.docId ? "Rejecting…" : "Confirm Reject"}
                          </button>
                          <button onClick={() => { setRejectingId(null); setRejectReason(""); }} style={btnGhost}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                        <button
                          onClick={() => handleApprove(n)}
                          disabled={actingId === n.docId}
                          style={{ ...btnPrimary, background: "#2ecc71" }}
                        >
                          {actingId === n.docId ? "Approving…" : "✓ Approve"}
                        </button>
                        <button onClick={() => setRejectingId(n.docId)} style={btnGhost}>
                          ✕ Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
export default function BylawsScreen() {
  return (
    <div style={{ padding: "16px", maxWidth: "640px", margin: "0 auto" }}>
      <div style={{ marginBottom: "14px" }}>
        <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>📜 Bylaws</div>
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
          Society bylaws, MC oath & representative nominations
        </div>
      </div>

      <BylawsPDFSection />
      <MCOathSection />
      <RepresentativeFormSection />
    </div>
  );
}
